import { useState, useEffect, useCallback } from 'react';
import { Alerta } from '../types';
import { apiService } from '../services/api';
import { authService } from '../services/auth';

export const useAlerts = () => {
  const [alerts, setAlerts] = useState<Alerta[]>([]);
  const [clearedAlerts, setClearedAlerts] = useState<Alerta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const activeAlerts = await apiService.getAllAlerts();
      setAlerts(activeAlerts);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Error al cargar alertas');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClearedAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resolved = await apiService.getClearedAlerts();
      setClearedAlerts(resolved);
    } catch (err) {
      console.error('Error fetching cleared alerts:', err);
      setError('Error al cargar historial de alertas');
    } finally {
      setLoading(false);
    }
  }, []);

  const clearAlert = useCallback(async (alertId: string) => {
    const user = authService.getUser();
    if (!user) {
      setError('Usuario no autenticado');
      return false;
    }

    try {
      const success = await apiService.clearAlert(alertId, user.email);
      if (success) {
        await fetchAlerts();
      }
      return success;
    } catch (err) {
      console.error('Error clearing alert:', err);
      setError('Error al resolver alerta');
      return false;
    }
  }, [fetchAlerts]);

  const deleteAlert = useCallback(async (alertId: string) => {
    try {
      const success = await apiService.deleteAlert(alertId);
      if (success) {
        await fetchAlerts();
        await fetchClearedAlerts();
      }
      return success;
    } catch (err) {
      console.error('Error deleting alert:', err);
      setError('Error al eliminar alerta');
      return false;
    }
  }, [fetchAlerts, fetchClearedAlerts]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  return {
    alerts,
    clearedAlerts,
    loading,
    error,
    fetchAlerts,
    fetchClearedAlerts,
    clearAlert,
    deleteAlert,
  };
};
